<template>
  <b-tab :title="title">
    <template slot="title">
      <span ref="title" class="font-size-md active text__tab-name">{{ title }}</span>

      <div class="divider" />
    </template>
    <div class="mt-u2 d-flex">
      <b-button
        variant="primary"
        class="mb-2 button__assign"
        :disabled="!selected.length"
        @click="showModal = true"
        >Assign {{ selected.length ? `(${selected.length})` : '' }}</b-button
      >
      <div class="flex-grow-1 ml-u6">
        <SearchBar
          v-model="searchKeywords"
          placeholder="Search submission ID here"
          @search="fetchEvidenceSubmission"
          @reset="reset"
        />
      </div>
    </div>
    <assign-assessor-modal
      :show.sync="showModal"
      :evidence-submission-ids="selected"
      @assign-success="assigned"
    />

    <transition name="slide-fade">
      <selectable-submission-list
        v-if="!isLoading"
        v-model="currentPage"
        :total-items="totalItems"
        :evidence-submissions="searchResults"
        :per-page="perPage"
        :selected-submissions.sync="selected"
      />
    </transition>
    <div v-if="isLoading" class="d-flex justify-content-center mt-5">
      <b-spinner label="Loading..." variant="dark" />
    </div>
  </b-tab>
</template>

<script>
import SelectableSubmissionList from '../../../components/EvidenceSubmission/SelectableSubmissionList.vue';
import SearchBar from '@/views/LearningUnit/components/SearchBar.vue';
import AssignAssessorModal from '../../../components/EvidenceSubmission/AssignAssessorModal.vue';

import EvidenceSubmission from '@/models/EvidenceSubmission';
import LearningUnit from '@/models/LearningUnit';
import { debounce, kebabCase } from 'lodash';

export default {
  components: {
    SelectableSubmissionList,
    SearchBar,
    AssignAssessorModal
  },
  props: {
    active: {
      type: [Number, Boolean],
      default: false
    },
    title: {
      type: String,
      default: 'Assign assessor'
    }
  },

  data() {
    return {
      showModal: false,
      selected: [],
      eventLogs: [],

      isLoading: true,
      fields: [
        { key: 'code', label: 'SUBMISSION ID', thClass: 'bg-white' },
        { key: 'submitted_no', label: 'SUBMITTED NO.', thClass: 'bg-white' },
        { key: 'submitted_at', label: 'SUBMITTED DATE', thClass: 'bg-white' },
        { key: 'status', label: 'STATUS', thClass: 'bg-white' },
        { key: 'action', label: '', thClass: 'bg-white' }
      ],
      filterOption: this.$route.query?.status
        ? this.$route.query.status.replaceAll('-', '_').toUpperCase()
        : 'ALL',
      searchKeywords: [],
      searchResults: [],

      totalItems: 0,
      currentPage: this.$route.query?.page ? parseInt(this.$route.query.page) : 1,
      perPage: 20
    };
  },

  computed: {
    fallbackRoute() {
      return {
        name: 'design-planning/info',
        params: {
          learningUnitId: this.learningUnitId
        },
        query: {
          tab: 'evaluation-assessments'
        }
      };
    },

    learningUnitId() {
      return parseInt(this.$route.params.learningUnitId);
    },

    learningUnit() {
      return LearningUnit.find(this.learningUnitId);
    },

    evidenceSubmissions() {
      return EvidenceSubmission.query()
        .where({ learning_unit_id: this.learningUnitId })
        .with('learning_unit')
        .orderBy('submitted_at', 'desc')
        .get();
    },

    filterOptions() {
      return [
        { value: null, text: 'All' },
        {
          value: 'REVIEW_PENDING',
          text: EvidenceSubmission.STATUS.REVIEW_PENDING
        },
        { value: 'DENIED', text: EvidenceSubmission.STATUS.DENIED },
        { value: 'AWARDED', text: EvidenceSubmission.STATUS.AWARDED }
      ];
    }
  },

  watch: {
    active: {
      immediate: true,
      handler(active) {
        if (active) {
          this.setDefaultValues();
          this.init();
        }
      }
    },

    currentPage: {
      handler(currentPage, previousPage) {
        if (currentPage) {
          this.fetchEvidenceSubmission({ currentPage });
        }

        if (currentPage != previousPage && previousPage != undefined) {
          this.$router.replace({
            query: { ...this.$route.query, page: currentPage }
          });
        }
      }
    },

    filterOption: {
      immediate: false,
      handler(filterOption) {
        this.$router.replace({
          query: { ...this.$route.query, status: kebabCase(filterOption) }
        });
      }
    }
  },

  created() {
    this.fetchEvidenceSubmission = debounce(this.fetchEvidenceSubmission, 100, {
      leading: true,
      trailing: false
    });
  },

  methods: {
    setDefaultValues() {
      this.selected = [];
      this.currentPage = 1;
      this.searchKeywords = [];
      this.filterOption = 'ALL';
    },

    async init() {
      const { currentPage } = this;

      this.isLoading = true;

      /* fetch learning unit */
      if (!this.learningUnit) {
        await LearningUnit.api().fetchByLearningUnitId(this.learningUnitId);
      }

      /* fetch evidence submission */
      EvidenceSubmission.deleteAll();

      await this.fetchEvidenceSubmission({ currentPage });

      this.isLoading = false;
    },

    async fetchEvidenceSubmission({ currentPage = 1 }) {
      this.isLoading = true;
      this.currentPage = currentPage;
      const filter = {
        _start: (this.currentPage - 1) * this.perPage,
        _limit: this.perPage,
        status_ne: 'NOT_SUBMITTED',
        _sort: 'submitted_at:desc',
        submitted_at_null: false,
        assessor_reviewed_at_null: true
      };

      const populate = ['assessor'];

      if (this.searchKeywords.length && !this.searchKeywords.includes('')) {
        const searchParams = this.searchKeywords.map((keyword) => keyword.toUpperCase());

        filter.code_contains = searchParams;
      }

      if (this.learningUnitId) {
        filter['learning_unit.id'] = this.learningUnitId;
      }

      if (this.filterOption !== 'ALL') {
        filter.status = this.filterOption;
      }

      if (this.filterOption == 'IN_PROGRESS_BY_ASSESSOR') {
        filter.status = 'REVIEW_PENDING';
        filter.assessor_reviewed_at_null = true;
      }

      if (this.filterOption == 'IN_PROGRESS_BY_ISSUER') {
        filter.status = 'REVIEW_PENDING';
        filter.assessor_reviewed_at_null = false;
        filter.issuer_reviewed_at_null = true;
      }

      /* fetch evidence submission */
      const { data, totalItems } = await LearningUnit.api().fetchEvidenceSubmission(
        this.learningUnitId,
        {
          ...filter,
          populate
        }
      );

      if (data && data.length) {
        EvidenceSubmission.insertOrUpdate({ data });
      }

      this.totalItems = totalItems;
      this.searchResults = data;

      this.isLoading = false;
    },

    reset() {
      this.searchKeywords = [];
      this.fetchEvidenceSubmission({ currentPage: 1 });
    },
    async assigned() {
      const { currentPage } = this;
      this.selected = [];
      await this.fetchEvidenceSubmission({ currentPage });
    }
  }
};
</script>

<style lang="scss" scoped></style>
